import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Cookie, X } from 'lucide-react';

const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      setShowBanner(true);
    }
  }, []);

  const acceptAll = () => {
    localStorage.setItem('cookie-consent', JSON.stringify({
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString()
    }));
    setShowBanner(false);
  };

  const acceptNecessary = () => {
    localStorage.setItem('cookie-consent', JSON.stringify({
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    }));
    setShowBanner(false);
  };

  const savePreferences = () => {
    const functional = document.getElementById('cookie-functional')?.checked || false;
    const analytics = document.getElementById('cookie-analytics')?.checked || false;
    const marketing = document.getElementById('cookie-marketing')?.checked || false;

    localStorage.setItem('cookie-consent', JSON.stringify({
      necessary: true,
      functional,
      analytics,
      marketing,
      timestamp: new Date().toISOString()
    }));
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-[100] flex items-end md:items-center justify-center p-4">
      <Card className="max-w-2xl w-full bg-[#0D1424] border-[rgba(232,203,122,0.25)] shadow-2xl rounded-3xl">
        <div className="p-8 space-y-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-3">
              <Cookie className="h-6 w-6 text-white" />
              <h3 className="text-xl font-semibold text-white">Cookie-Einstellungen</h3>
            </div>
            <button onClick={acceptNecessary} aria-label="Schließen" data-testid="cookie-close-btn" className="text-[#A9B4C7] hover:text-white transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>

          <p className="text-[#A9B4C7] leading-relaxed">
            Ich verwende Cookies, damit die Website für dich gut funktioniert. 
            Einige Cookies sind technisch notwendig, andere helfen mir, mein Angebot zu verbessern.
          </p>

          {!showDetails ? (
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button 
                onClick={acceptAll} data-testid="cookie-accept-all-btn" 
                className="flex-1 gold-btn text-[#070B14] rounded-full min-h-[48px]"
              >
                Alle akzeptieren
              </Button>
              <Button 
                onClick={acceptNecessary} data-testid="cookie-necessary-btn"
                variant="outline" 
                className="flex-1 border-[rgba(232,203,122,0.25)] bg-transparent text-white hover:bg-white/5 rounded-full min-h-[48px]"
              >
                Nur notwendige
              </Button>
              <Button 
                onClick={() => setShowDetails(true)} data-testid="cookie-customize-btn" 
                variant="outline" 
                className="flex-1 border-[rgba(232,203,122,0.25)] bg-transparent text-white hover:bg-white/5 rounded-full min-h-[48px]"
              >
                Anpassen
              </Button>
            </div>
          ) : (
            <div className="space-y-4 pt-4">
              <div className="space-y-3">
                <div className="flex items-center justify-between p-4 bg-white/5 rounded-2xl">
                  <div>
                    <p className="font-semibold text-white">Notwendige Cookies</p>
                    <p className="text-sm text-[#A9B4C7]">Erforderlich für die Funktion der Website</p>
                  </div>
                  <input type="checkbox" checked disabled className="h-5 w-5" />
                </div>

                <div className="flex items-center justify-between p-4 bg-white/5 rounded-2xl">
                  <div>
                    <p className="font-semibold text-white">Funktionale Cookies</p>
                    <p className="text-sm text-[#A9B4C7]">Speichern deine Einstellungen</p>
                  </div>
                  <input 
                    id="cookie-functional"
                    type="checkbox" 
                    defaultChecked 
                    className="h-5 w-5" 
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-white/5 rounded-2xl">
                  <div>
                    <p className="font-semibold text-white">Analyse-Cookies</p>
                    <p className="text-sm text-[#A9B4C7]">Helfen mir, die Website zu verbessern</p>
                  </div>
                  <input 
                    id="cookie-analytics"
                    type="checkbox" 
                    className="h-5 w-5" 
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-white/5 rounded-2xl">
                  <div>
                    <p className="font-semibold text-white">Marketing-Cookies</p>
                    <p className="text-sm text-[#A9B4C7]">Für personalisierte Werbung</p>
                  </div>
                  <input 
                    id="cookie-marketing"
                    type="checkbox" 
                    className="h-5 w-5" 
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  onClick={savePreferences} 
                  className="flex-1 gold-btn text-[#070B14] rounded-full min-h-[48px]"
                >
                  Auswahl speichern
                </Button>
                <Button 
                  onClick={() => setShowDetails(false)} 
                  variant="outline" 
                  className="flex-1 border-[rgba(232,203,122,0.25)] bg-transparent text-white hover:bg-white/5 rounded-full min-h-[48px]"
                >
                  Zurück
                </Button>
              </div>
            </div>
          )}

          <p className="text-xs text-[#A9B4C7] pt-2">
            Mehr dazu findest du in meiner{' '}
            <a href="/datenschutz" className="text-[#E8CB7A] hover:underline">Datenschutzerklärung</a>.
          </p>
        </div>
      </Card>
    </div>
  );
};

export default CookieBanner;